import { yupResolver } from "@hookform/resolvers/yup";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import InputMask, {
  type BeforeMaskedStateChangeStates,
} from "react-input-mask";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import brazilianBanks from "../../mocks/brazilianBanks";
import {
  bankDataSchema,
  validateBankDataOrPixIsFilled,
} from "../../validators/bankDataSchema";
import { Button } from "../Button";
import { FormGroup } from "../FormElements/FormGroup";
import { Input } from "../FormElements/Input";
import { SelectWithSearch } from "../SelectWithSearch";
import { useExpertQuery } from "../../services/hooks/experts/queries/useExpertQuery";
import { PaymentTypeEnum } from "../../enumns/paymentType";

type BankDataProps = {
  isSubmitting: boolean;
  defaultValues?: FormValues;
  onSubmit: (data: FormValues) => void;
  onCancel?: () => void;
  onGoPreviousStep?: () => void;
  userId?: number;
};

type FormValues = {
  bank: string;
  bankBranch: string;
  currentAccount: string;
  pix: string;
};

export const BankDataForm = ({
  isSubmitting,
  defaultValues,
  onSubmit,
  onGoPreviousStep,
  userId,
}: BankDataProps) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const { data } = useExpertQuery(userId!);

  const bankDataYupSchema =
    data?.paymentType !== PaymentTypeEnum.NO_PAYMENT
      ? bankDataSchema.test(
          "at-least-one",
          "INVALID_BANK_DATA",
          validateBankDataOrPixIsFilled
        )
      : bankDataSchema;

  const { control, formState, handleSubmit, setValue } = useForm<FormValues>({
    resolver: yupResolver(bankDataYupSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues,
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const brazilianBankOptions = useMemo(() => {
    return brazilianBanks.map(({ COMPE, LongName }) => ({
      label: `${COMPE} - ${LongName}`,
      value: `${COMPE} - ${LongName}`,
    }));
  }, [brazilianBanks]);

  const handleBeforeMaskedBankBranchChangeStates = ({
    nextState,
  }: BeforeMaskedStateChangeStates) => {
    let { value } = nextState;

    if (value.length === 5) {
      value = value.replace(/\D+/g, "");
    }

    return {
      ...nextState,
      value: value || "",
    };
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="max-w-[415px] flex flex-col gap-2"
    >
      <div className="mb-4 flex gap-4 items-end">
        <div className="grow">
          <Controller
            name="bank"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <FormGroup
                label={formatMessage({ id: "BANK" })}
                error={error?.message && formatMessage({ id: error.message })}
              >
                <SelectWithSearch
                  value={value}
                  onValueChange={onChange}
                  defaultValue={value}
                  options={brazilianBankOptions}
                  searchPlaceholder={formatMessage({ id: "SEARCH_BY_BANK" })}
                  noButton
                />
              </FormGroup>
            )}
          />
        </div>
        <Button
          size="x-small"
          className="p-[4px] mb-4"
          onClick={() => setValue("bank", "")}
          style={{
            padding: "8px",
            fontSize: "10px",
          }}
        >
          <span>X</span>
        </Button>
      </div>
      <div className="flex items-baseline gap-4 mb-4">
        <div className="grow">
          <Controller
            name="bankBranch"
            control={control}
            render={({
              field: { value = "", onChange },
              fieldState: { error, invalid, isDirty },
            }) => (
              <InputMask
                mask="9999-9"
                maskPlaceholder={null}
                value={value}
                onChange={onChange}
                beforeMaskedStateChange={
                  handleBeforeMaskedBankBranchChangeStates
                }
              >
                <Input
                  label={formatMessage({ id: "BANK_BRANCH" })}
                  placeholder="0000"
                  isValid={!invalid && isDirty}
                  errorMessage={
                    error?.message && formatMessage({ id: error.message })
                  }
                />
              </InputMask>
            )}
          />
        </div>

        <div className="grow">
          <Controller
            name="currentAccount"
            control={control}
            render={({
              field: { value = "", onChange },
              fieldState: { error, invalid, isDirty },
            }) => (
              <Input
                value={value}
                onChange={onChange}
                label={formatMessage({ id: "CURRENT_ACCOUNT" })}
                placeholder="00000-0"
                isValid={!invalid && isDirty}
                maxLength={10}
                errorMessage={
                  error?.message && formatMessage({ id: error.message })
                }
                onBlur={() => {
                  const maskedValue = value
                    .replace(/\D/g, "")
                    .replace(/^(\d+)(\d)$/, "$1-$2");
                  setValue("currentAccount", maskedValue);
                }}
              />
            )}
          />
        </div>
      </div>

      <div className="mb-4">
        <Controller
          name="pix"
          control={control}
          render={({
            field: { value = "", onChange },
            fieldState: { error, invalid, isDirty },
          }) => (
            <Input
              value={value}
              onChange={onChange}
              label={formatMessage({ id: "PIX" })}
              placeholder="Insira sua chave PIX"
              isValid={!invalid && isDirty}
              errorMessage={
                error?.message && formatMessage({ id: error.message })
              }
            />
          )}
        />
      </div>
      <div className="flex flex-row mt-14 gap-4 justify-center">
        <Button
          type="button"
          onClick={onGoPreviousStep || (() => navigate("/expert"))}
          variant="outline"
          borderColor="border-primary-light"
          size="large"
          className="ps-12 pe-12"
        >
          {formatMessage({ id: "BACK" })}
        </Button>

        <Button
          type="submit"
          isLoading={isSubmitting}
          disabled={
            !formState.isValid &&
            data?.paymentType !== PaymentTypeEnum.NO_PAYMENT
          }
          size="large"
          className="ps-12 pe-12"
        >
          {formatMessage({ id: "SAVE" })}
        </Button>
      </div>
    </form>
  );
};
